import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { getLogger } from '../../libs/logger';

export const logger = getLogger('PatientTransferSlice');

export type PatientTransfer = {
  patientId: string;
  /** Room the patient is being transferred to */
  roomId: string;
  slug: string;
  roomName: string;
  /** Room the patient is being transferred from */
  fromRoomId: string;
  /** True when the patient was in a call (status was Busy) before the transfer started */
  wasInACall?: boolean;
  startedAt?: number;
  completedAt?: number;
};

export type PatientTransferState = {
  patientId: string | undefined;
  transfers: Record<string, PatientTransfer>;
};

/* istanbul ignore next */
export const getInitialState = (): PatientTransferState => ({
  patientId: undefined,
  transfers: {},
});

/* istanbul ignore next */
export const patientTransferSlice = createSlice({
  name: 'patientTransfer',
  initialState: () => getInitialState(),
  reducers: {
    setPatientTransferPatientId: (
      state: Draft<PatientTransferState>,
      action: PayloadAction<string | undefined>,
    ): void => {
      state.patientId = action.payload;
    },
    resetPatientTransferPatientId: (
      state: Draft<PatientTransferState>,
    ): void => {
      state.patientId = undefined;
    },
    setTransfer: (
      state: Draft<PatientTransferState>,
      action: PayloadAction<PatientTransfer>,
    ): void => {
      state.transfers[action.payload.patientId] = {
        startedAt: Date.now(),
        ...action.payload,
      };
    },
    removeTransfer: (
      state: Draft<PatientTransferState>,
      action: PayloadAction<string>,
    ): void => {
      delete state.transfers[action.payload];
    },
    markTransferAsCompleted: (
      state: Draft<PatientTransferState>,
      action: PayloadAction<string>,
    ): void => {
      const patient = state.transfers[action.payload];

      if (patient) {
        patient.completedAt = Date.now();
      }
    },
  },
});

export const {
  setPatientTransferPatientId,
  resetPatientTransferPatientId,
  setTransfer,
  removeTransfer,
  markTransferAsCompleted,
} = patientTransferSlice.actions;

export { addTransfer } from './thunks/addTransfer';
